import ValidatedForm from "components/forms/ValidatedForm";
import validationSchemas from "utils/validationSchemas";
import ImageCropper from "./imageCropper/ImageCropper";
import fileUtils from "utils/fileUtils";
import ImageAspectRatio from "enums/imageAspectRatio";
import { type FC } from "react";
import type { AnySchema } from "yup";

import styles from "./uploadImage.module.scss";

export interface Props {
  propertyName?: string;
  onCroppedImageChange: (image: Blob | null) => void;
  file: string;
  onFileChanged: (file: ArrayBuffer | string | null) => void;
  fileName: File[];
  onFileNameChanged: (fileName: FileList) => void;
  aspectRatio?: ImageAspectRatio;
  getImageCustomValidator?: () => { validator: AnySchema; propName: string };
  showBestCrop?: boolean;
}
const UploadImage: FC<Props> = ({
  propertyName = "uploadedImages",
  onCroppedImageChange,
  file,
  onFileChanged,
  fileName,
  onFileNameChanged,
  aspectRatio = ImageAspectRatio.HD_16X9,
  getImageCustomValidator,
  showBestCrop,
}) => {
  const handleCroppedImageChange = (image: Blob | null) => {
    onCroppedImageChange && onCroppedImageChange(image);
  };

  const handleFileChange = (files: FileList) => {
    if (needToUpdateFile(files)) {
      // to-do remove here usage of callback. Use useEffect and update Formik related logic to pass arguments
      onFileNameChanged(files);
    }

    const custom = getImageCustomValidator?.();
    const validator: AnySchema = custom
      ? custom.validator
      : (validationSchemas.image.fields.uploadedImages as unknown as AnySchema);
    return validator
      .validate(files)
      .then(() => {
        fileUtils.readFileAsDataUrl(files[0]).then((result) => {
          onFileChanged(result);
        });
      })
      .catch(() => {
        onFileChanged(null);
        handleCroppedImageChange(null);
      });
  };

  const needToUpdateFile = (uploadedFiles: FileList) => {
    if (!uploadedFiles || !uploadedFiles.length) {
      return false;
    }
    if (!fileName.length) {
      return true;
    }
    return fileUtils.isDifferentFile(uploadedFiles[0], fileName[0]);
  };

  return (
    <>
      <ValidatedForm initialValues={{ uploadedImages: {} }} validationSchema={validationSchemas.image}>
        {(formProps) => {
          const { validatedFieldProps } = formProps;
          return (
            <ValidatedForm.UploadFileField
              id="imageUpload"
              label="File"
              accept="image/*"
              propertyName={getImageCustomValidator ? getImageCustomValidator().propName : propertyName}
              value={fileName}
              onChangeHandler={handleFileChange}
              filesValidator={
                getImageCustomValidator
                  ? getImageCustomValidator().validator
                  : validationSchemas.image.fields.uploadedImages
              }
              {...validatedFieldProps}
            />
          );
        }}
      </ValidatedForm>
      {showBestCrop && (
        <span className={styles["best-results"]}>
          PNG, JPG, GIF, SVG (max 5 MB). Suggested dimensions: min 200 px width.
        </span>
      )}
      {file && <ImageCropper file={file} onCroppedImageChange={handleCroppedImageChange} aspectRatio={aspectRatio} />}
    </>
  );
};

export default UploadImage;
